import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatCurrency, formatApiErrorDetail } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { Plus, Trash, ArrowLeft, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function QuoteEditor() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { settings } = useSettings();

  const [clients, setClients] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [form, setForm] = useState({
    cliente_id: "",
    data_emissione: new Date().toISOString().slice(0, 10),
    data_validita: "",
    items: [],
    iva_percent: 22,
    rivalsa_percent: 0,
    rivalsa_label: "Rivalsa cassa previdenziale",
    ritenuta_percent: 0,
    ritenuta_label: "Ritenuta d'acconto",
    note: "",
    condizioni: "",
    termini_pagamento: "",
    metodo_pagamento: "",
    consenti_abbonamento: false,
    abbonamento_rate_count: 12,
    disable_auto_reminders: false,
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [cs, cat] = await Promise.all([api.get("/clients"), api.get("/catalogo")]);
      setClients(cs.data);
      setCatalogo((cat.data || []).filter(s => s.attivo));
      if (isEdit) {
        try {
          const { data } = await api.get(`/quotes/${id}`);
          setForm({
            cliente_id: data.cliente_id,
            data_emissione: (data.data_emissione || "").slice(0, 10),
            data_validita: (data.data_validita || "").slice(0, 10),
            items: data.items || [],
            iva_percent: data.iva_percent ?? 22,
            rivalsa_percent: data.rivalsa_percent ?? 0,
            rivalsa_label: data.rivalsa_label || "Rivalsa cassa previdenziale",
            ritenuta_percent: data.ritenuta_percent ?? 0,
            ritenuta_label: data.ritenuta_label || "Ritenuta d'acconto",
            note: data.note || "",
            condizioni: data.condizioni || "",
            termini_pagamento: data.termini_pagamento || "",
            metodo_pagamento: data.metodo_pagamento || "",
            consenti_abbonamento: !!data.consenti_abbonamento,
            abbonamento_rate_count: data.abbonamento_rate_count || 12,
            disable_auto_reminders: !!data.disable_auto_reminders,
          });
        } catch (e) {
          toast.error("Preventivo non trovato");
          navigate("/preventivi");
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [id, isEdit, navigate]);

  // Apply settings defaults for NEW quotes (only first time settings load)
  useEffect(() => {
    if (!isEdit && settings) {
      setForm((f) => ({
        ...f,
        iva_percent: f.iva_percent === 22 ? (settings.iva_default ?? 22) : f.iva_percent,
        condizioni: f.condizioni || settings.condizioni_default || "",
        note: f.note || settings.note_default || "",
        termini_pagamento: f.termini_pagamento || (settings.termini_pagamento?.[0] || ""),
        metodo_pagamento: f.metodo_pagamento || (settings.metodi_pagamento?.[0] || ""),
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const totals = useMemo(() => {
    let sub = 0;
    form.items.forEach((it) => {
      const q = Number(it.quantita || 0);
      const p = Number(it.prezzo_unitario || 0);
      const s = Number(it.sconto_percent || 0);
      sub += q * p * (1 - s / 100);
    });
    const rivalsa = sub * (Number(form.rivalsa_percent || 0) / 100);
    const imponibile = sub + rivalsa;
    const iva = imponibile * (Number(form.iva_percent || 0) / 100);
    const ritenuta = imponibile * (Number(form.ritenuta_percent || 0) / 100);
    const totale = imponibile + iva - ritenuta;
    return { subtotale: sub, rivalsa_importo: rivalsa, imponibile_iva: imponibile, iva_totale: iva, ritenuta_importo: ritenuta, totale };
  }, [form.items, form.iva_percent, form.rivalsa_percent, form.ritenuta_percent]);

  function addItem(templateService = null) {
    if (templateService) {
      setForm((f) => {
        const next = {
          ...f,
          items: [...f.items, {
            descrizione: templateService.nome,
            quantita: 1,
            prezzo_unitario: templateService.prezzo || 0,
            sconto_percent: 0,
            note: templateService.descrizione || "",
            codice: templateService.codice,
            unita: templateService.unita,
          }],
        };
        // Auto-attiva pagamento rateale se il servizio è marcato come "abbonabile"
        if (templateService.consenti_abbonamento) {
          next.consenti_abbonamento = true;
          next.abbonamento_rate_count = templateService.abbonamento_rate_count || 12;
          // Toast informativo
          setTimeout(() => toast.success(`"${templateService.codice}" attiva il pagamento rateale mensile (${next.abbonamento_rate_count} rate)`), 100);
        }
        return next;
      });
    } else {
      setForm((f) => ({
        ...f,
        items: [...f.items, { descrizione: "", quantita: 1, prezzo_unitario: 0, sconto_percent: 0, note: "" }],
      }));
    }
  }
  function updateItem(idx, key, value) {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, [key]: value } : it)),
    }));
  }
  function removeItem(idx) {
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  }

  async function save() {
    if (!form.cliente_id) return toast.error("Seleziona un cliente");
    if (form.items.length === 0) return toast.error("Aggiungi almeno una voce");
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: form.items.map((it) => ({
          ...it,
          quantita: Number(it.quantita),
          prezzo_unitario: Number(it.prezzo_unitario),
          sconto_percent: Number(it.sconto_percent),
        })),
        iva_percent: Number(form.iva_percent),
        rivalsa_percent: Number(form.rivalsa_percent) || 0,
        rivalsa_label: form.rivalsa_label || "Rivalsa cassa previdenziale",
        ritenuta_percent: Number(form.ritenuta_percent) || 0,
        ritenuta_label: form.ritenuta_label || "Ritenuta d'acconto",
        data_emissione: form.data_emissione ? new Date(form.data_emissione).toISOString() : null,
        data_validita: form.data_validita ? new Date(form.data_validita).toISOString() : null,
        termini_pagamento: form.termini_pagamento || null,
        metodo_pagamento: form.metodo_pagamento || null,
        consenti_abbonamento: !!form.consenti_abbonamento,
        abbonamento_rate_count: Number(form.abbonamento_rate_count) || 12,
        disable_auto_reminders: !!form.disable_auto_reminders,
      };
      let result;
      if (isEdit) {
        const r = await api.put(`/quotes/${id}`, payload);
        result = r.data;
      } else {
        const r = await api.post("/quotes", payload);
        result = r.data;
      }
      toast.success("Preventivo salvato");
      navigate(`/preventivi/${result.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="p-12 text-sm text-[#64748B]">Caricamento…</div>;

  return (
    <div className="p-4 md:p-12 max-w-[1400px] mx-auto">
      <button onClick={() => navigate(-1)} className="text-xs uppercase tracking-[0.15em] text-[#64748B] hover:text-[#0A0A0A] flex items-center gap-2 mb-6">
        <ArrowLeft size={14} /> Indietro
      </button>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3">{isEdit ? "Modifica" : "Nuovo"}</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-none">Preventivo</h1>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-quote-btn"
          className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2 self-start"
        >
          <FloppyDisk size={14} weight="bold" /> {saving ? "Salvataggio…" : "Salva"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="swiss-card p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-4">Intestazione</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Cliente *">
                <select
                  required
                  value={form.cliente_id}
                  onChange={(e) => setForm({ ...form, cliente_id: e.target.value })}
                  data-testid="quote-client-select"
                  className="w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
                >
                  <option value="">— Seleziona —</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.ragione_sociale}</option>
                  ))}
                </select>
                {clients.length === 0 && (
                  <button type="button" onClick={() => navigate("/clienti")} className="mt-2 text-xs text-[#0033A0] hover:underline">
                    Aggiungi prima un cliente →
                  </button>
                )}
              </Field>
              <Field label="IVA %">
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={form.iva_percent}
                  onChange={(e) => setForm({ ...form, iva_percent: e.target.value })}
                  className="w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
                  data-testid="quote-iva-input"
                />
              </Field>
              <Field label="Data Emissione">
                <input
                  type="date"
                  value={form.data_emissione}
                  onChange={(e) => setForm({ ...form, data_emissione: e.target.value })}
                  className="w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
                  data-testid="quote-date-input"
                />
              </Field>
              <Field label="Valido Fino al">
                <input
                  type="date"
                  value={form.data_validita}
                  onChange={(e) => setForm({ ...form, data_validita: e.target.value })}
                  className="w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
                />
              </Field>
            </div>
          </div>

          {/* Fiscalità — Rivalsa cassa + Ritenuta d'acconto (opzionali, per professionisti) */}
          <div className="swiss-card p-6 border-l-4 border-l-[#FF9800]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9800] font-bold">Fiscalità</div>
                <h3 className="font-display text-lg font-bold tracking-tight mt-1">Rivalsa & Ritenuta d'acconto</h3>
              </div>
            </div>
            <p className="text-xs text-[#64748B] mb-4">Lascia a 0 se non applicabili. Esempio professionista in regime ordinario: <strong>Rivalsa 4%</strong> (Gestione Separata INPS) + <strong>Ritenuta 20%</strong>.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Field label="Rivalsa cassa previdenziale (%)">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step="0.5"
                    value={form.rivalsa_percent}
                    onChange={(e) => setForm({ ...form, rivalsa_percent: e.target.value })}
                    className="w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm font-mono focus:border-[#FF9800] focus:ring-1 focus:ring-[#FF9800] outline-none"
                    data-testid="quote-rivalsa-input"
                    placeholder="es. 4"
                  />
                </Field>
                {Number(form.rivalsa_percent) > 0 && (
                  <input
                    type="text"
                    value={form.rivalsa_label}
                    onChange={(e) => setForm({ ...form, rivalsa_label: e.target.value })}
                    className="mt-2 w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-xs focus:border-[#FF9800] outline-none"
                    placeholder="Etichetta (es. Rivalsa 4% INPS GS)"
                    data-testid="quote-rivalsa-label"
                  />
                )}
              </div>
              <div>
                <Field label="Ritenuta d'acconto (%)">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step="0.5"
                    value={form.ritenuta_percent}
                    onChange={(e) => setForm({ ...form, ritenuta_percent: e.target.value })}
                    className="w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm font-mono focus:border-[#FF9800] focus:ring-1 focus:ring-[#FF9800] outline-none"
                    data-testid="quote-ritenuta-input"
                    placeholder="es. 20"
                  />
                </Field>
                {Number(form.ritenuta_percent) > 0 && (
                  <input
                    type="text"
                    value={form.ritenuta_label}
                    onChange={(e) => setForm({ ...form, ritenuta_label: e.target.value })}
                    className="mt-2 w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-xs focus:border-[#FF9800] outline-none"
                    placeholder="Etichetta (es. Ritenuta 20%)"
                    data-testid="quote-ritenuta-label"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="swiss-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B]">Voci Preventivo</div>
              <div className="flex gap-2">
                <select
                  onChange={(e) => {
                    const s = catalogo.find(c => c.id === e.target.value);
                    if (s) { addItem(s); e.target.value = ""; }
                  }}
                  className="text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm px-2 py-1.5 bg-white max-w-[200px]"
                  data-testid="quote-template-select"
                >
                  <option value="">+ Da Catalogo ({catalogo.length})</option>
                  {Array.from(new Set(catalogo.map(c => c.categoria).filter(Boolean))).map(cat => (
                    <optgroup key={cat} label={cat}>
                      {catalogo.filter(s => s.categoria === cat).map(s => (
                        <option key={s.id} value={s.id}>{s.codice} · {s.nome} · €{s.prezzo}</option>
                      ))}
                    </optgroup>
                  ))}
                  {catalogo.filter(s => !s.categoria).map(s => (
                    <option key={s.id} value={s.id}>{s.codice} · {s.nome} · €{s.prezzo}</option>
                  ))}
                </select>
                <button
                  onClick={() => addItem(null)}
                  data-testid="add-quote-item-btn"
                  className="px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold border border-[#0A0A0A] rounded-sm hover:bg-[#0A0A0A] hover:text-white flex items-center gap-1.5 transition-all"
                >
                  <Plus size={12} weight="bold" /> Voce
                </button>
              </div>
            </div>

            {form.items.length === 0 ? (
              <div className="py-12 text-center border border-dashed border-[#E2E8F0]">
                <p className="text-sm text-[#64748B]">Nessuna voce. Aggiungi un servizio dal catalogo o personalizzato.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {form.items.map((it, idx) => (
                  <div key={idx} className="border border-[#E2E8F0] rounded-sm p-3" data-testid={`quote-item-${idx}`}>
                    <div className="grid grid-cols-12 gap-2">
                      <input
                        placeholder="Descrizione servizio"
                        value={it.descrizione}
                        onChange={(e) => updateItem(idx, "descrizione", e.target.value)}
                        className="col-span-12 md:col-span-6 bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
                      />
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="Q.tà"
                        value={it.quantita}
                        onChange={(e) => updateItem(idx, "quantita", e.target.value)}
                        className="col-span-3 md:col-span-1 bg-white border border-[#E2E8F0] rounded-sm px-2 py-2 text-sm text-right font-mono focus:border-[#0033A0] outline-none"
                      />
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="Prezzo €"
                        value={it.prezzo_unitario}
                        onChange={(e) => updateItem(idx, "prezzo_unitario", e.target.value)}
                        className="col-span-4 md:col-span-2 bg-white border border-[#E2E8F0] rounded-sm px-2 py-2 text-sm text-right font-mono focus:border-[#0033A0] outline-none"
                      />
                      <input
                        type="number"
                        min={0}
                        max={100}
                        placeholder="Sconto %"
                        value={it.sconto_percent}
                        onChange={(e) => updateItem(idx, "sconto_percent", e.target.value)}
                        className="col-span-3 md:col-span-1 bg-white border border-[#E2E8F0] rounded-sm px-2 py-2 text-sm text-right font-mono focus:border-[#0033A0] outline-none"
                      />
                      <div className="col-span-12 md:col-span-2 text-right text-sm font-mono font-bold flex items-center justify-end">
                        {formatCurrency(
                          (Number(it.quantita) || 0) *
                          (Number(it.prezzo_unitario) || 0) *
                          (1 - (Number(it.sconto_percent) || 0) / 100)
                        )}
                      </div>
                      <button onClick={() => removeItem(idx)} className="col-span-2 md:col-span-12 md:col-start-13 self-center p-1 text-[#64748B] hover:text-[#D32F2F]" data-testid={`remove-item-${idx}`}>
                        <Trash size={16} />
                      </button>
                    </div>
                    <input
                      placeholder="Note (opzionale)"
                      value={it.note || ""}
                      onChange={(e) => updateItem(idx, "note", e.target.value)}
                      className="w-full mt-2 bg-transparent text-xs text-[#64748B] px-1 py-1 focus:outline-none border-b border-transparent focus:border-[#E2E8F0]"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="swiss-card p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-4">Pagamento</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Termini di Pagamento">
                <select
                  value={form.termini_pagamento || ""}
                  onChange={(e) => setForm({ ...form, termini_pagamento: e.target.value })}
                  data-testid="quote-termini-select"
                  className="w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
                >
                  <option value="">— Seleziona —</option>
                  {(settings?.termini_pagamento || []).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Metodo di Pagamento">
                <select
                  value={form.metodo_pagamento || ""}
                  onChange={(e) => setForm({ ...form, metodo_pagamento: e.target.value })}
                  data-testid="quote-metodo-select"
                  className="w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
                >
                  <option value="">— Seleziona —</option>
                  {(settings?.metodi_pagamento || []).map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
            </div>
            {(settings?.termini_pagamento?.length === 0 && settings?.metodi_pagamento?.length === 0) && (
              <p className="text-xs text-[#64748B] mt-3">
                Nessuna opzione configurata. <a href="/impostazioni" className="text-[#0033A0] underline">Configurale nelle Impostazioni →</a>
              </p>
            )}
          </div>

          <div className="swiss-card p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-4">Note & Condizioni</div>
            <Field label="Note per il cliente">
              <textarea
                rows={3}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
                data-testid="quote-notes-input"
              />
            </Field>
            <div className="h-3" />
            <Field label="Condizioni di pagamento e validità">
              <textarea
                rows={3}
                value={form.condizioni}
                onChange={(e) => setForm({ ...form, condizioni: e.target.value })}
                className="w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
              />
            </Field>
          </div>

          {/* Abbonamento rateale — opt-in per preventivo (es. SIS-DIG) */}
          <div className="swiss-card p-6 border-l-4 border-l-[#008A3C]">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#008A3C] font-bold mb-1">Pagamenti Online</div>
                <h3 className="font-display text-lg font-bold tracking-tight">Pagamento rateale mensile</h3>
                <p className="text-xs text-[#64748B] mt-1">Abilita questa opzione <strong>solo per servizi annuali</strong> (es. SIS-DIG software). Il cliente potrà scegliere di pagare in rate mensili invece del totale.</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                <input
                  type="checkbox"
                  checked={!!form.consenti_abbonamento}
                  onChange={(e) => setForm({ ...form, consenti_abbonamento: e.target.checked })}
                  className="sr-only peer"
                  data-testid="consenti-abbonamento-toggle"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-[#008A3C]/30 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#008A3C]"></div>
              </label>
            </div>
            {form.consenti_abbonamento && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-[#E2E8F0]">
                <Field label="Numero rate mensili">
                  <input
                    type="number"
                    min={2}
                    max={36}
                    step="1"
                    value={form.abbonamento_rate_count}
                    onChange={(e) => setForm({ ...form, abbonamento_rate_count: e.target.value })}
                    className="w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#008A3C] focus:ring-1 focus:ring-[#008A3C] outline-none font-mono"
                    data-testid="rate-count-input"
                  />
                </Field>
                <div className="text-xs bg-[#008A3C]/5 rounded-sm p-3 leading-relaxed">
                  <strong className="text-[#008A3C]">Esempio</strong>: con totale {totals.totale.toFixed(2)}€ → <strong className="font-mono">{(totals.totale / Number(form.abbonamento_rate_count || 12)).toFixed(2)}€/mese</strong> per {form.abbonamento_rate_count} mesi. Stop automatico al termine.
                </div>
              </div>
            )}
          </div>

          <div className="swiss-card p-6 border-l-4 border-l-[#FF9800]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9800] font-bold mb-1">Follow-up</div>
                <h3 className="font-display text-lg font-bold tracking-tight">Reminder automatici</h3>
                <p className="text-xs text-[#64748B] mt-1">Se attivi (default), il cliente riceverà 3 promemoria educati dopo <strong>3, 7 e 14 giorni</strong> dall&apos;invio finché non firma. Disattivali per preventivi in trattativa offline o casi speciali.</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                <input
                  type="checkbox"
                  checked={!form.disable_auto_reminders}
                  onChange={(e) => setForm({ ...form, disable_auto_reminders: !e.target.checked })}
                  className="sr-only peer"
                  data-testid="auto-reminders-toggle"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-[#FF9800]/30 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF9800]"></div>
              </label>
            </div>
            {form.disable_auto_reminders && (
              <div className="mt-3 pt-3 border-t border-[#E2E8F0] text-xs text-[#64748B] bg-[#FFF4E5] p-3 rounded-sm">
                ⚠️ Reminder disattivati per questo preventivo. Potrai sempre inviare reminder manuali dalla lista Preventivi.
              </div>
            )}
          </div>
        </div>

        {/* Right: totals */}
        <div className="lg:col-span-1">
          <div className="swiss-card p-6 sticky top-6 bg-[#0A0A0A] text-white border-[#0A0A0A]">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 mb-4">Riepilogo</div>
            <div className="space-y-3 text-sm">
              <Row label="Subtotale" value={formatCurrency(totals.subtotale)} />
              {Number(form.rivalsa_percent) > 0 && (
                <Row label={`+ Rivalsa ${form.rivalsa_percent}%`} value={formatCurrency(totals.rivalsa_importo)} />
              )}
              <Row label={`IVA ${form.iva_percent}%`} value={formatCurrency(totals.iva_totale)} />
              {Number(form.ritenuta_percent) > 0 && (
                <Row label={`− Ritenuta ${form.ritenuta_percent}%`} value={`− ${formatCurrency(totals.ritenuta_importo)}`} />
              )}
              <div className="h-px bg-white/20 my-2" />
              <Row label="TOTALE" value={formatCurrency(totals.totale)} big />
            </div>
            <div className="mt-6 pt-6 border-t border-white/20 text-[10px] uppercase tracking-[0.2em] text-white/60">
              {form.items.length} voci · IVA {form.iva_percent}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
function Row({ label, value, big }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`uppercase tracking-[0.15em] ${big ? "text-xs font-bold" : "text-[10px] text-white/70"}`}>{label}</span>
      <span className={`font-mono font-bold ${big ? "text-2xl" : "text-sm"}`}>{value}</span>
    </div>
  );
}
